import type { FC, ReactNode, RefObject } from 'react'

import { useEffect, useRef, useState } from 'react'
import { Pause, Play } from 'lucide-react'
import { cn } from '@/lib/utils'

interface NetworkInformationLike {
    saveData?: boolean
}

interface AmbientVideoOptions {
    src: string
    // Start right away (hero). Otherwise the video only plays while it is on screen.
    priority?: boolean
    hasPoster?: boolean
}

interface AmbientVideoState {
    ready: boolean
    failed: boolean
    playing: boolean
    toggle: () => void
    videoProps: {
        ref: RefObject<HTMLVideoElement | null>
        muted: true
        loop: true
        playsInline: true
        preload: 'none'
        tabIndex: -1
        'aria-hidden': true
        onPlaying: () => void
        onPause: () => void
        onLoadedData: () => void
        onError: () => void
    }
}

// Muted, looping background video. Respects reduced motion and data saver, reports when a frame
// is available so the element can fade in, and flags files the browser cannot play.
export const useAmbientVideo = ({ src, priority = false, hasPoster = false }: AmbientVideoOptions): AmbientVideoState => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const pausedByUser = useRef(false)
    // With a poster the element is visible right away; without one it fades in once a frame is available.
    const [ready, setReady] = useState(hasPoster)
    const [failed, setFailed] = useState(false)
    const [playing, setPlaying] = useState(false)

    useEffect(() => {
        const video = videoRef.current

        if (!video) return

        video.muted = true

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection
        const saveData = Boolean(connection?.saveData)

        // Data saver: load nothing until asked. Reduced motion: show the first frame as a still.
        if (saveData || reducedMotion) {
            pausedByUser.current = true

            if (!saveData) {
                video.preload = 'metadata'
                video.load()
            }

            return
        }

        const play = () => {
            if (pausedByUser.current) return

            video.preload = 'auto'
            video.play().catch(() => setPlaying(false))
        }

        if (priority) {
            play()

            return
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry?.isIntersecting) play()
            else video.pause()
        }, { rootMargin: '120px 0px' })

        observer.observe(video)

        return () => observer.disconnect()
    }, [priority, src])

    const toggle = () => {
        const video = videoRef.current

        if (!video) return

        if (video.paused) {
            pausedByUser.current = false
            video.preload = 'auto'
            video.play().catch(() => setPlaying(false))
        }

        else {
            pausedByUser.current = true
            video.pause()
        }
    }

    return {
        ready,
        failed,
        playing,
        toggle,
        videoProps: {
            ref: videoRef,
            muted: true,
            loop: true,
            playsInline: true,
            preload: 'none',
            tabIndex: -1,
            'aria-hidden': true,
            onPlaying: () => {
                setReady(true)
                setPlaying(true)
            },
            onPause: () => setPlaying(false),
            onLoadedData: () => setReady(true),
            onError: () => setFailed(true)
        }
    }
}

interface VideoToggleProps {
    playing: boolean
    onToggle: () => void
    className?: string
}

// Pause and play control for background video (WCAG 2.2.2).
export const VideoToggle: FC<VideoToggleProps> = ({ playing, onToggle, className }): ReactNode => {
    return (
        <button
            type='button'
            onClick={onToggle}
            aria-label={playing ? 'Hintergrundvideo pausieren' : 'Hintergrundvideo abspielen'}
            className={cn(
                'flex size-11 shrink-0 items-center justify-center rounded-full border border-bone/30 bg-ink/30 text-bone backdrop-blur-sm transition-colors duration-300 hover:border-bone/70 hover:bg-ink/50',
                className
            )}
        >
            {playing
                ? <Pause className='size-4' strokeWidth={2} />
                : <Play className='size-4 translate-x-px' strokeWidth={2} />}
        </button>
    )
}

interface AmbientVideoProps {
    src: string
    poster?: string | null
    priority?: boolean
    className?: string
}

export const AmbientVideo: FC<AmbientVideoProps> = ({ src, poster, priority = false, className }): ReactNode => {
    const { ready, failed, videoProps } = useAmbientVideo({ src, priority, hasPoster: Boolean(poster) })

    if (failed) return null

    return (
        <video
            src={src}
            poster={poster ?? undefined}
            {...videoProps}
            className={cn(
                'absolute inset-0 size-full object-cover transition-opacity duration-[1400ms] ease-brand',
                ready ? 'opacity-100' : 'opacity-0',
                className
            )}
        />
    )
}
